/**
 * useSubscription — центральный хук подписки.
 *
 * Является единственным источником истины о том:
 * - какой план активен у текущей компании
 * - какие функции разрешены (feature gates)
 * - не истекла ли подписка
 * - сколько дней до истечения
 *
 * Использование:
 *   const { hasFeature, isExpired, daysLeft, plan } = useSubscription();
 *   if (!hasFeature('analytics')) return <UpgradePrompt />;
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuthStore } from '../../domains/auth/authStore';
import { getCompanyLimits } from '../superadmin/api-subscriptions';
import { isSupabaseConfigured } from '../../domains/auth/supabaseClient';

// ─── Feature definitions ──────────────────────────────────────────────────────

/**
 * Полный список feature-ключей, которые могут быть включены/выключены планом.
 * Эти строки хранятся в subscription_plans.features[].
 */
export type FeatureKey =
  | 'offline_mode'        // Офлайн-режим и sync
  | 'push_notifications'  // Push-уведомления
  | 'photo_upload'        // Загрузка фото
  | 'signature_capture'   // Подпись клиента
  | 'analytics'           // Раздел аналитики (KPI, тренды, рейтинги)
  | 'checklist_builder'   // Конструктор чеклистов
  | 'repeat_orders'       // Повторные выезды / follow-up
  | 'multi_dispatcher'    // Несколько диспетчеров
  | 'api_access'          // API-доступ
  | 'white_label'         // White-label брендирование
  | 'online_payments';    // 1.27.0 — Онлайн-эквайринг через ЮKassa (с комиссией Кворум)

/**
 * Функции, доступные при истёкшей подписке (grace / expired).
 * Намеренно пустой массив — при истечении подписки все фичи блокируются.
 * Если в будущем нужно оставить какие-то функции доступными в grace-период,
 * добавить их сюда.
 */
const ALWAYS_ENABLED: FeatureKey[] = [];

// ─── Types ────────────────────────────────────────────────────────────────────

export type SubscriptionStatus = 'active' | 'trial' | 'grace' | 'expired' | 'cancelled' | 'suspended' | 'none';

export interface SubscriptionState {
  /** Загрузка данных */
  loading: boolean;
  /** Ошибка загрузки */
  error: string | null;
  /** Текущий статус подписки */
  status: SubscriptionStatus;
  /** Истекла или нет (expired / cancelled / suspended / none) */
  isExpired: boolean;
  /** На пробном периоде */
  isTrial: boolean;
  /** Активна ли подписка (active или trial) */
  isActive: boolean;
  /** Дней до истечения (null = бессрочная) */
  daysLeft: number | null;
  /** Скоро истекает (≤ 7 дней) */
  expiresSoon: boolean;
  /** Название плана */
  planName: string | null;
  /** Максимум пользователей (null = без лимита) */
  maxUsers: number | null;
  /** Список включённых функций */
  features: string[];
  /**
   * 1.19.2 — TRUE если в текущей сессии хотя бы один успешный fetch
   * лимитов состоялся. FALSE при холодном старте оффлайн или пока
   * первый запрос ещё не закончился. Critical-path экраны (Signature)
   * не должны принимать решения о skip без этого флага.
   */
  hasFreshData: boolean;
  /** Проверить доступность конкретной функции */
  hasFeature: (key: FeatureKey) => boolean;
  /** Принудительно перезагрузить данные о подписке */
  refresh: () => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

// 1.19.2 — Кэш последнего успешного снапшота подписки в localStorage.
// Зачем: при оффлайне getCompanyLimits бросает ошибку (см. Patch 1) →
// useSubscription уходит в error-state. Без кэша features становятся
// пустыми, что ломает feature-gating (Signature, FeatureGate и т.д.).
// С кэшем — при оффлайне используем последний известный валидный
// снапшот, а при возврате сети load() перезапишет его.
type SubscriptionSnapshot = {
  status: SubscriptionStatus;
  endDate: string | null;
  planName: string | null;
  maxUsers: number | null;
  features: string[];
};

const SNAPSHOT_KEY_PREFIX = 'fieldsnap-sub-snapshot:';

function loadSnapshot(companyId: string | null): SubscriptionSnapshot | null {
  if (!companyId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(`${SNAPSHOT_KEY_PREFIX}${companyId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SubscriptionSnapshot>;
    // Минимальная валидация формы — features обязан быть массивом.
    if (!Array.isArray(parsed.features)) return null;
    return {
      status: (parsed.status as SubscriptionStatus) ?? 'none',
      endDate: parsed.endDate ?? null,
      planName: parsed.planName ?? null,
      maxUsers: parsed.maxUsers ?? null,
      features: parsed.features as string[],
    };
  } catch {
    return null;
  }
}

function saveSnapshot(companyId: string | null, snapshot: SubscriptionSnapshot) {
  if (!companyId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      `${SNAPSHOT_KEY_PREFIX}${companyId}`,
      JSON.stringify(snapshot),
    );
  } catch {
    // Quota / private mode — игнорируем, на функционал не влияет.
  }
}

export function useSubscription(): SubscriptionState {
  const user = useAuthStore((state) => state.user);
  const companyId = user?.company_id ?? null;

  // Восстанавливаем последний снапшот СИНХРОННО на первом рендере.
  // Это значит что даже при холодном старте офлайн (компании уже
  // знаком клиент, но supabase недоступен) features будут на месте.
  const initialSnapshot = useMemo(() => loadSnapshot(companyId), [companyId]);
  const [data, setData] = useState<SubscriptionSnapshot | null>(initialSnapshot);
  // loading=true только если снапшота нет вообще — иначе UI сразу
  // получает рабочее состояние, а фоновое обновление подменит его.
  const [loading, setLoading] = useState(initialSnapshot === null);
  const [error, setError] = useState<string | null>(null);
  // 1.19.2 — флаг, что хотя бы один успешный fetch состоялся в этой
  // сессии. Нужен Signature.tsx чтобы не делать skip без подтверждения.
  const [hasFreshData, setHasFreshData] = useState(false);

  const load = useCallback(() => {
    if (!companyId || !isSupabaseConfigured) {
      // В demo-режиме все функции разрешены
      const demoSnapshot: SubscriptionSnapshot = {
        status: 'active',
        endDate: null,
        planName: 'Demo',
        maxUsers: null,
        features: [
          'offline_mode', 'push_notifications', 'photo_upload',
          'signature_capture', 'analytics', 'checklist_builder',
          'repeat_orders', 'multi_dispatcher',
        ],
      };
      setData(demoSnapshot);
      setHasFreshData(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    getCompanyLimits(companyId)
      .then((limits) => {
        if (!limits) {
          // Нет подписки — это валидный успешный ответ сервера.
          const emptySnapshot: SubscriptionSnapshot = {
            status: 'none',
            endDate: null,
            planName: null,
            maxUsers: null,
            features: [],
          };
          setData(emptySnapshot);
          saveSnapshot(companyId, emptySnapshot);
          setHasFreshData(true);
          return;
        }

        const snapshot: SubscriptionSnapshot = {
          status: (limits.subscription_status as SubscriptionStatus) ?? 'none',
          endDate: limits.subscription_end_date,
          planName: limits.plan_name ?? null,
          maxUsers: limits.max_users ?? null,
          features: limits.features, // нормализовано в getCompanyLimits
        };
        setData(snapshot);
        saveSnapshot(companyId, snapshot);
        setHasFreshData(true);
      })
      .catch((e: unknown) => {
        // 1.16.1 / 1.19.2 — Сетевая ошибка ≠ истёкшая подписка.
        //   • Если в state уже есть data (из кэша или предыдущего fetch),
        //     оставляем как есть — пилот продолжает работать.
        //   • Если data нет вообще — это значит ни кэша, ни первой
        //     успешной загрузки. UI получит loading=false + error≠null
        //     + data=null, и derived isExpired останется false
        //     (см. условие ниже).
        //   • Главное: features НЕ обнуляем, и hasFreshData остаётся
        //     false — Signature.tsx по нему понимает что skip небезопасен.
        setError(e instanceof Error ? e.message : 'Ошибка загрузки подписки');
      })
      .finally(() => setLoading(false));
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  // 1.16.1 — Автоматический re-fetch когда возвращается сеть.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onOnline = () => {
      // Перезагружаем при возврате сети независимо от текущего состояния
      // — обновим status, planName, features.
      load();
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [load]);

  // ── Derived state ──────────────────────────────────────────────────────────

  const status: SubscriptionStatus = data?.status ?? 'none';
  const isActive = status === 'active' || status === 'trial' || status === 'grace';
  const isTrial = status === 'trial';
  // 1.16.1 / 1.19.2 / 1.19.3 — isExpired = TRUE только если сервер ЯВНО
  // подтвердил истечение. Условия:
  //   • hasFreshData — в этой сессии прошёл успешный fetch с сервера.
  //   • !error — последний fetch прошёл успешно (сетевая ошибка ≠ истечение).
  //   • status в expired/cancelled/suspended.
  //
  // 1.19.3 — Важное изменение: status='none' БОЛЬШЕ НЕ считается истечением.
  // 'none' означает что в company_subscriptions нет ни одной строки для
  // этой компании. Это происходит в трёх случаях:
  //   1) Бета-пилот: суперадмин ещё не назначил тариф этой компании, но
  //      пилоты уже работают.
  //   2) Self-hosted dev/sandbox: миграции применены, но subscription_plans
  //      ещё не наполнены.
  //   3) Реально неоплатная компания: суперадмин удалил подписку.
  // Кейсы 1-2 — это рабочее состояние пилота, не блокируем функционал.
  // Кейс 3 — суперадмин/админ должен это исправить через UI, и пока он не
  // исправил, лучше дать продолжить работу, чем терять подписи/фото.
  // Реальная блокировка происходит только при явных expired/cancelled/suspended.
  const isExpired = hasFreshData && !error &&
    (status === 'expired' || status === 'cancelled' || status === 'suspended');

  const daysLeft: number | null = (() => {
    if (!data?.endDate) return null;
    const diff = new Date(data.endDate).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  })();

  const expiresSoon = daysLeft !== null && daysLeft <= 7 && isActive;

  const features = data?.features ?? [];

  // 1.19.3 — Sandbox-режим. Если у компании status='none' (нет записи в
  // company_subscriptions), считаем что фичи не настроены и разрешаем всё.
  // Это решает связку: пилот тестирует приложение, у компании нет
  // подписки в БД → features=[] → hasFeature('signature_capture')=false →
  // Signature.tsx пропускает экран → подпись теряется. Без этой защиты
  // приходится создавать подписку в каждой dev/staging среде, что
  // блокирует тест.
  const isSandbox = status === 'none';

  const hasFeature = useCallback(
    (key: FeatureKey): boolean => {
      // В demo-режиме всё разрешено
      if (!isSupabaseConfigured) return true;
      // 1.19.3 — Sandbox (компания без подписки в БД): всё разрешено.
      if (isSandbox) return true;
      // Если подписка неактивна — только базовые функции
      if (isExpired) return ALWAYS_ENABLED.includes(key);
      // Базовые функции всегда доступны при активной подписке
      if (ALWAYS_ENABLED.includes(key)) return true;
      // Проверяем по списку из плана
      return features.includes(key);
    },
    [isExpired, isSandbox, features],
  );

  return {
    loading,
    error,
    status,
    isExpired,
    isTrial,
    isActive,
    daysLeft,
    expiresSoon,
    planName: data?.planName ?? null,
    maxUsers: data?.maxUsers ?? null,
    features,
    hasFreshData,
    hasFeature,
    refresh: load,
  };
}
